import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { http, unwrap } from './client'
import { streamEvents } from './event-stream'
import { retryUnlessRefused } from './workspaces'
import type { ManagementFunction, MetricReadiness } from '@/types/intelligence'
import type { ApiEnvelope, DataProvenanceMode, InferenceStatus } from '@/types/api'
import type { PeriodKind, PeriodSelection } from './snapshot'

/*
 * The Conversational AI Analyst (P4), as the Intelligence Engine (S1) serves
 * it. These shapes are copied from the backend's
 * `src/intelligence/dto/intelligence-object.dto.ts`, which decides: change
 * them there first.
 */

/** S0 §6: the classes an answer may carry. */
export type AnswerClaimClass = 'DERIVED_FACT' | 'ASSOCIATION' | 'DIAGNOSTIC_HYPOTHESIS'

/** S1 Step 1: the five kinds of question. */
export type IntentType =
  | 'METRIC_LOOKUP'
  | 'DIAGNOSTIC_INVESTIGATION'
  | 'COHORT_COMPARISON'
  | 'QUALITY_SURVEILLANCE'
  | 'TOOL_UTILIZATION'

/** S1 §2's nine steps, in order. */
export type PipelineStep =
  | 'INTENT'
  | 'PLAN'
  | 'METRICS'
  | 'DATA'
  | 'EXECUTE'
  | 'INTERPRET'
  | 'VERIFY'
  | 'PACKAGE'
  | 'AUDIT'

export type Breakdown = 'VISIT_TYPE' | 'PROFESSIONAL_ROLE' | 'HOUR_OF_DAY' | 'WEEKDAY'

export type AnswerOutcome =
  | 'ANSWERED'
  | 'NOT_MEASURABLE'
  | 'CLINICAL_QUESTION'
  | 'ABOUT_A_PERSON'
  | 'OUT_OF_SCOPE'
  | 'WITHHELD'

export type AnalystEvent =
  | {
      type: 'step'
      step: PipelineStep
      status: 'STARTED' | 'DONE'
      attempt: number
      metricIds: string[]
      /** The period's first and last day, once Step 1 has resolved it. */
      period: { first: string; last: string } | null
    }
  | { type: 'answer'; answer: AnalystAnswer }
  | { type: 'error'; code: string; message: string }

export interface AnswerPeriod {
  kind: PeriodKind
  first: string
  last: string
  baselineFirst: string
  baselineLast: string
  timezone: string
  timezoneKnown: boolean
}

export interface AnswerStatement {
  claimClass: AnswerClaimClass
  /** The model's words, in the reader's language, passed by the hard gate. */
  text: string
  metricIds: string[]
  factIds: string[]
  sampleN: number | null
  notices: Array<'ASSOCIATION_NOT_CAUSATION' | 'NEEDS_VALIDATION'>
  /** A property of the method behind an ASSOCIATION, never a score of the finding (S0 §10.1). */
  statistic: { methodId: string; kind: 'PEARSON_R'; value: number; n: number } | null
}

export type FactMeasure =
  /** The count itself; for a care-journey share, those meeting it. */
  | 'VALUE'
  | 'BASELINE'
  /** A care-journey share, 0 to 1, of the `n` patients, results or care loops it applies to. */
  | 'SHARE'
  | 'CHANGE_ABSOLUTE'
  | 'CHANGE_RELATIVE'
  | 'P25'
  | 'P50'
  | 'P75'
  /** P75 less P25. */
  | 'IQR'
  /** Pearson r of the daily median against a covariate. */
  | 'PEARSON_R'

export interface Fact {
  factId: string
  metricId: string
  methodId: string
  measure: FactMeasure
  window: 'CURRENT' | 'BASELINE' | 'CHANGE'
  group: { breakdown: Breakdown; key: string } | null
  /** In `unit`; CHANGE_RELATIVE is a fraction (0.583 is +58.3%); a share's CHANGE_ABSOLUTE is in percentage points. */
  value: number
  unit: 'COUNT' | 'MINUTES' | 'FRACTION' | 'COEFFICIENT' | 'PERCENTAGE_POINTS'
  /** Measured waits for a median or a group, days for a correlation. */
  n: number | null
  /** For PEARSON_R: what the daily median was set against. */
  covariate: 'VISITS_STARTED_PER_DAY' | null
}

export interface MetricReading {
  metricId: string
  readiness: MetricReadiness
  /** Why it is not READY, as codes: the Overview's MetricNote codes. */
  limitations: string[]
}

export interface NotMeasurable {
  /** A metric id, a method id or a concept code (`CONCEPT-TRIAGE`). */
  subject: string
  /** NOT_RECORDED: the period is before the workspace's first record. */
  reason: 'NOT_REGISTERED' | 'NO_SOURCE' | 'TOO_FEW' | 'SOURCE_UNAVAILABLE' | 'NOT_RECORDED'
  /** Source codes that would make it measurable (`ELFIECARE:TRIAGE_EVENTS`). */
  missing: string[]
}

export interface Suppression {
  breakdown: Breakdown
  groups: number
  minimum: number
}

export type HardGateCheck =
  | 'NUMERIC_CONSISTENCY'
  | 'CLAIM_TIER'
  | 'READINESS'
  | 'MINIMUM_COHORT'
  | 'DATA_BOUNDARY'
  | 'PRIORITIZATION_INTEGRITY'
  | 'ATTRIBUTION'

/** S1 Step 7's outcome. Present only when the gate passed in an OPERATIONAL workspace. */
export interface VerificationResult {
  passed: true
  gateVersion: string
  checkedAt: string
  checks: HardGateCheck[]
  checkedFactsCount: number
  extractedNumbers: number[]
  claimClassesApproved: AnswerClaimClass[]
  /** Statements whose causal wording the gate relabelled as a hypothesis (S1 Step 7 check 2). */
  downgradedStatements: number
}

/** S0 §26b.5: declared context the answer materially depends on. */
export interface AttestedDependencyMarker {
  attestedContextId: string
  attestedBy: string
  asOf: string
}

export interface AnalystAnswer {
  answerId: string
  conversationId: string
  askedAt: string
  outcome: AnswerOutcome
  intent: { type: IntentType; metricIds: string[]; breakdown: Breakdown | null } | null
  period: AnswerPeriod | null
  statements: AnswerStatement[]
  facts: Fact[]
  metrics: MetricReading[]
  notMeasurable: NotMeasurable[]
  suppressions: Suppression[]
  verification: VerificationResult | null
  attestedContext: AttestedDependencyMarker[]
  inference: { mode: string; provider: string; region: string | null } | null
  /** The take-aways the engine prepared: each from the first statement about its metric (S1 Step 8). */
  actions: ActionShortcut[]
  dataProvenanceMode: DataProvenanceMode
  /** The function the reader had declared when they asked, which Elfie wrote for; null for the institution's view. */
  readerFunction: ManagementFunction | null
}

export interface ActionShortcut {
  primitive: 'SHARE_WITH_NOTE' | 'ASSIGN_FOLLOW_UP' | 'SET_AS_OBJECTIVE' | 'SAVE_EXPORT_REPORT'
  metricId: string
  statementIndex: number
}

/** Whether the Analyst can answer in this workspace, and where it would (P4 §5.3). */
export interface AnalystAvailability {
  status: InferenceStatus
  arrangement: { mode: string; provider: string; region: string | null } | null
  dataProvenanceMode: DataProvenanceMode
}

/** The screens a question can be asked from, and what each was showing (P4 §3): every analysis page. */
export type AnalystSurface = 'OVERVIEW' | 'OPPORTUNITIES' | 'OPPORTUNITY' | 'OBJECTIVES' | 'REPORTS' | 'REPORT' | 'ATTENTION'

export interface ScreenContext {
  surface: AnalystSurface
  period: PeriodSelection
  metricId?: string
}

export interface AnalystQuestion {
  question: string
  conversationId?: string
  context: ScreenContext
}

export const analystKeys = {
  availability: (workspaceId: string) => ['workspaces', workspaceId, 'analyst', 'availability'] as const,
}

export const useAnalystAvailability = (workspaceId: MaybeRefOrGetter<string | undefined>) =>
  useQuery({
    queryKey: computed(() => analystKeys.availability(toValue(workspaceId) ?? '')),
    queryFn: async () =>
      unwrap(
        (await http.get<ApiEnvelope<AnalystAvailability>>(`/workspaces/${toValue(workspaceId)}/analyst/availability`)).data,
      ),
    enabled: computed(() => !!toValue(workspaceId)),
    retry: retryUnlessRefused,
  })

/** Asks one question; the run's steps and then its answer arrive through `onEvent`. */
export const askAnalyst = (
  workspaceId: string,
  question: AnalystQuestion,
  onEvent: (event: AnalystEvent) => void,
  signal?: AbortSignal,
): Promise<void> => streamEvents<AnalystEvent>(`/workspaces/${workspaceId}/analyst/questions`, question, onEvent, signal)
